import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvConfirm,
	EvIcon,
	EvSection,
	EvSelectNew,
	EvTextInput,
	EvTitle,
	RemoveIcon
} from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from 'src/consts/dom-consts';
import {
	createDefaultRuleInUIFormat,
	getAllRulesInUIFormat
} from 'src/helpers/UrlDiscoveryScopeHelper';
import { IScopeRuleUIFormat } from 'src/interfaces/ScopeRuleUIFormat';
import { IAllowedDeniedFilter } from 'src/interfaces/UrlSetDiscoveryConfiguration';
import { SCOPE_ACTIONS } from 'src/types/scope/ScopeActions';
import { SCOPE_TYPES } from 'src/types/scope/ScopeTypes';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { usePropertySettings } from '../context';
import { setScope } from '../state';

import {
	ARIA_LABELS,
	DROP_DOWN_OPTIONS,
	INPUT_PLACEHOLDERS,
	SCOPE_SETTINGS_DESCRIPTION_ID,
	SCOPE_SETTINGS_TITLE_ID
} from './ScopeSettingsV2.constants';
import { SCOPE_LABELS, SCOPE_MORE_INFO } from './ScopeSettingsV2.labels';

import '../../../components/property-settings/sections/scope-settings/ScopeSettings.scss';

function getAllowedDeniedRules(
	rules: IScopeRuleUIFormat[],
	type: SCOPE_TYPES
): IAllowedDeniedFilter {
	return {
		allowed: rules
			.filter((r) => r.scopeType === type && r.scopeAction === SCOPE_ACTIONS.INCLUDE)
			.map((r) => r.url),
		denied: rules
			.filter((r) => r.scopeType === type && r.scopeAction === SCOPE_ACTIONS.EXCLUDE)
			.map((r) => r.url)
	};
}

export const ScopeSettingsV2: FC = () => {
	const {
		state: { draft: property, errors },
		dispatch
	} = usePropertySettings();

	const propertyError = useMemo(
		() => errors.find((e) => e.sectionId === SETTINGS_SECTION_TYPES.SCOPE),
		[errors]
	);
	const [allRules, setAllRules] = useState<IScopeRuleUIFormat[]>(
		getAllRulesInUIFormat(property.urlSetConfiguration.discoveryConfiguration)
	);
	const [lastAddedRuleId, setLastAddedRuleId] = useState<string | null>(null);

	const dispatchScope = useCallback(
		(rules: IScopeRuleUIFormat[]): void => {
			dispatch(
				setScope(
					getAllowedDeniedRules(rules, SCOPE_TYPES.LANG),
					getAllowedDeniedRules(rules, SCOPE_TYPES.URL),
					getAllowedDeniedRules(rules, SCOPE_TYPES.DOMAIN)
				)
			);
		},
		[dispatch]
	);

	const removeScopeRule = useCallback(
		(ruleIdToDelete: string): void => {
			const newRules = allRules.filter((r) => r.id !== ruleIdToDelete);
			setAllRules(newRules);
			dispatchScope(newRules);
		},
		[allRules, dispatchScope]
	);

	const addDomainRule = useCallback((): void => {
		const newRule = createDefaultRuleInUIFormat(SCOPE_TYPES.DOMAIN);
		const newRules = [...allRules, newRule];
		setAllRules(newRules);
		setLastAddedRuleId(newRule.id);
		dispatchScope(newRules);
	}, [allRules, dispatchScope]);

	const addLanguageRule = useCallback((): void => {
		const newRule = createDefaultRuleInUIFormat(SCOPE_TYPES.LANG);
		const newRules = [...allRules, newRule];
		setAllRules(newRules);
		setLastAddedRuleId(newRule.id);
		dispatchScope(newRules);
	}, [allRules, dispatchScope]);

	const addRegexRule = useCallback((): void => {
		const newRule = createDefaultRuleInUIFormat(SCOPE_TYPES.URL);
		const newRules = [...allRules, newRule];
		setAllRules(newRules);
		setLastAddedRuleId(newRule.id);
		dispatchScope(newRules);
	}, [allRules, dispatchScope]);

	const editScopeRuleAction = useCallback(
		(id: string, action: SCOPE_ACTIONS): void => {
			const newRules = allRules.map((r) => (r.id === id ? { ...r, scopeAction: action } : r));
			setAllRules(newRules);
			dispatchScope(newRules);
		},
		[allRules, dispatchScope]
	);

	const editScopeRuleTextValue = useCallback(
		(id: string, value: string): void => {
			const newRules = allRules.map((r) => (r.id === id ? { ...r, url: value } : r));
			setAllRules(newRules);
			dispatchScope(newRules);
		},
		[allRules, dispatchScope]
	);

	const handleConfirmDelete = useCallback(
		(ruleId: string, closeModal: () => void): void => {
			removeScopeRule(ruleId);
			closeModal();
		},
		[removeScopeRule]
	);

	return (
		<EvSection
			className="scope-settings-section"
			ariaLabelledby={SCOPE_SETTINGS_TITLE_ID}
			ariaDescribedby={SCOPE_SETTINGS_DESCRIPTION_ID}
		>
			<div className="scope-settings-content">
				<div className="section-header">
					<div className="title">
						<EvTitle
							titleText={SCOPE_LABELS.SECTION_TITLE}
							MoreInfoContent={SCOPE_MORE_INFO}
							id={SCOPE_SETTINGS_TITLE_ID}
							descriptionId={SCOPE_SETTINGS_DESCRIPTION_ID}
						/>
						<div className="actions">
							<div className="single-action">
								<EvButton
									title={SCOPE_LABELS.ADD_DOMAIN_RULE}
									onClick={addDomainRule}
									type={BUTTON_TYPES.ACTION}
								>
									{SCOPE_LABELS.ADD_DOMAIN}
								</EvButton>
							</div>
							<div className="single-action">
								<EvButton
									title={SCOPE_LABELS.ADD_LANGUAGE}
									onClick={addLanguageRule}
									type={BUTTON_TYPES.ACTION}
								>
									{SCOPE_LABELS.ADD_LANGUAGE}
								</EvButton>
							</div>
							<div className="single-action">
								<EvButton
									title={SCOPE_LABELS.ADD_REGEX_RULE}
									onClick={addRegexRule}
									type={BUTTON_TYPES.ACTION}
								>
									{SCOPE_LABELS.ADD_REGEX_RULE}
								</EvButton>
							</div>
						</div>
					</div>
				</div>
				<div className="section-body">
					<div className="rules">
						{allRules.map((rule: IScopeRuleUIFormat) => {
							const options = DROP_DOWN_OPTIONS[rule.scopeType];
							const selectedOption = options.find((o) => o.value === rule.scopeAction);
							return (
								<div className="rule-line" key={rule.id}>
									<label className="form-label">
										<EvSelectNew
											id={`scope-settings-select-${rule.id}`}
											options={options}
											selectedValue={[selectedOption]}
											onSelectChange={(selectedItem) => {
												editScopeRuleAction(rule.id, selectedItem[0]?.value as SCOPE_ACTIONS);
											}}
											placeholder={SCOPE_LABELS.SELECT_PLACEHOLDER}
											label={SCOPE_LABELS.SELECT_LABEL}
										/>
									</label>
									<div className="form-field">
										<EvTextInput
											autoFocus={rule.id === lastAddedRuleId}
											placeholder={INPUT_PLACEHOLDERS[rule.scopeType]}
											ariaLabel={ARIA_LABELS[rule.scopeType]}
											value={rule.url}
											onChange={(value) => editScopeRuleTextValue(rule.id, value)}
											error={rule.urlError}
										/>
									</div>
									<div className="rule-actions">
										<EvConfirm
											appElement={SITE_SCANNER_APP_ID}
											title={SCOPE_LABELS.DELETE_RULE_TITLE}
											triggerButtonProps={{
												type: BUTTON_TYPES.ICON,
												title: SCOPE_LABELS.DELETE_RULE_TITLE,
												className: 'rule-action',
												children: <EvIcon icon={RemoveIcon} />
											}}
											approveButtonText={SCOPE_LABELS.DELETE_BUTTON}
											promptMessage={SCOPE_LABELS.DELETE_CONFIRM_MESSAGE}
											onConfirm={(closeModal) => handleConfirmDelete(rule.id, closeModal)}
										/>
									</div>
								</div>
							);
						})}
					</div>
					{propertyError && <div className="tab-error">{propertyError.message}</div>}
				</div>
			</div>
		</EvSection>
	);
};
